/**
 * The panel shell's contracts: what a panel declares about itself, what the
 * runtime hands it back, and the snapshot the agent reads. Not wire shapes —
 * none of this leaves the browser.
 */
import type { ComponentType } from 'react';

import type { ActivityCount, RecentActivitySnapshot } from './events';
import type { ViewScope } from './viewScope';

/**
 * Every panel the app can open. The list is the source of the type, so a panel
 * joins the app by being named here and in `app/registry.ts` — and a misspelled
 * id stops being a string the runtime shrugs at. Alphabetical: `listPanels()`
 * order reaches the agent's tool catalog, so it should not shuffle.
 */
export const PANEL_IDS = [
    'agent',
    'ai_chat',
    'ai_search',
    'audit_log',
    'bookmarks',
    'camera_settings',
    'camera_view',
    'cameras_list',
    'case_details',
    'case_document',
    'case_image',
    'case_video',
    'enrolled_objects',
    'events_list',
    'health_monitor',
    'motion_list',
    'notifications',
    'objects_list',
    'organization_settings',
    'rule_estimate',
    'rule_flow',
    'rule_flow_details',
    'rules',
    'servers',
    'user_details',
    'users',
    'video_search',
] as const;
export type PanelId = (typeof PANEL_IDS)[number];

/**
 * Which half of the app a panel belongs to. A frozen object rather than an
 * enum because every panel.config declares it as bare strings
 * (`modes: ['view', 'settings']`), and a literal is not assignable to an enum
 * member without a cast at each of those declarations.
 */
export const PanelMode = {
    View: 'view',
    Settings: 'settings',
} as const;
export type PanelMode = (typeof PanelMode)[keyof typeof PanelMode];

/** Which colour themes a panel is willing to render under. */
export const PanelTheme = {
    Any: 'any',
    Light: 'light',
    Dark: 'dark',
} as const;
export type PanelTheme = (typeof PanelTheme)[keyof typeof PanelTheme];

/**
 * What a session actually renders in. Distinct from PanelTheme, which is what
 * a panel is willing to render under and may be 'any'. A frozen object for the
 * same reason: PanelHost puts a panel's own PanelTheme in its place.
 */
export const Theme = {
    Light: 'light',
    Dark: 'dark',
} as const;
export type Theme = (typeof Theme)[keyof typeof Theme];

/**
 * The theme that the user picks in the user menu. `System` follows the theme of the operating
 * system. A frozen object: storage holds a bare string.
 */
export const ThemePreference = {
    Light: 'light',
    Dark: 'dark',
    System: 'system',
} as const;
export type ThemePreference = (typeof ThemePreference)[keyof typeof ThemePreference];

/**
 * What Expand saves in place of a pixel width: the panel takes whatever room the
 * row has, on every stage, until a drag gives it a width again.
 */
export const MAX_PANEL_WIDTH = 'max';

/** A width the user gave a panel: pixels from a drag, or the row's room from Expand. */
export type SavedPanelWidth = number | typeof MAX_PANEL_WIDTH;

/** Widths the user gave panels, keyed by panel id. */
export type UserWidths = Record<string, SavedPanelWidth>;

/** A panel's pixel bounds on the stage. */
export interface PanelWidth {
    min: number;
    max: number;
    default: number;
    /** Takes the room left over once every other panel has its own. */
    fill: boolean;
    /** Displaces its neighbours rather than being squeezed below `default`. */
    keepsDefaultWidth: boolean;
}

/** The panel that summons another, and the props of it that name the place of its control. */
export interface PanelSummoner {
    id: PanelId;
    /** A write that changes one of these props closes the summoned panel. */
    props: string[];
}

/**
 * A panel's descriptor, as `definePanel` normalizes it — every optional field
 * of the author's config filled in, so readers never repeat a default.
 */
export interface PanelConfig {
    id: PanelId;
    title: string;
    /**
     * What the section is for and when to reach for it, in a sentence or two. The home
     * launcher shows it under the title, and a panel that declares one is a destination the
     * launcher offers.
     */
    description: string;
    /** Rendered as a component, e.g. a lucide icon. */
    icon?: ComponentType<{ className?: string }> | null;
    /**
     * The tabs that the home launcher offers as tiles, in place of one tile for the panel. The
     * nav rail opens the panel on the first.
     */
    launcherTabs: LauncherTab[];
    width: PanelWidth;
    /**
     * Width rules that depend on what the panel shows or on who shares the row,
     * layered over `width` while they apply. `row` is every open panel.
     */
    getWidth?: (
        props: Record<string, unknown>, row: OpenPanel[],
    ) => Partial<PanelWidth> | undefined;
    modes: PanelMode[];
    themes: PanelTheme;
    /**
   * Every registered panel this one cannot coexist with. Materialised by the
   * registry from exclusions.ts, never declared per panel.
   */
    excludes: string[];
    autoOpenIds: PanelId[];
    /** A panel id, or one per mode, opened beside this panel automatically. */
    autoChild?: PanelId | Partial<Record<PanelMode, PanelId>> | null;
    /**
     * The panel whose control opens this one. This panel closes with it, and when the place of
     * that control changes. A panel without one stays open across such changes, as the camera
     * view does.
     */
    summoner: PanelSummoner | null;
    previewTabs: string[];
    /**
   * Stays mounted, hidden, while the row has no room for it, so what the
   * operator had in it is there when the row hands it back. Off for the video,
   * which must not keep streaming behind another panel.
   */
    shouldKeepMounted: boolean;
    /**
   * Hosted beside the stage rather than on it — the chat drawer. Takes no room
   * in the row, and never the row's focus, except on a phone: there it is a
   * panel on the stage like the rest.
   */
    isOffStage: boolean;
    /**
     * Where the row has no room for the panel, it floats over the row at the stage's right edge
     * instead of being shed. It never anchors the row, so it never displaces a panel.
     */
    isOverlay: boolean;
    order: number;
    /** Place in the nav rail, where it differs from the stage `order`. */
    navOrder: number;
    /** The rail half holding the button: settings for a panel only settings mode holds. */
    navGroup: PanelMode;
    hasNavButton: boolean;
    requiresAdmin: boolean;
    isHeaderless: boolean;
    isTransparent: boolean;
    /**
     * The panel shows the card selected in another panel, so it wears the surface of a selected
     * card: the card fill, the brand edge and the brand ring.
     */
    hasSelectedSurface: boolean;
    hasVideo: boolean;
    shouldClip: boolean;
    /** Props that ride the URL's search params. */
    urlSyncedProps: string[];
    /**
     * Props naming what the panel is editing. An open changing one departs the panel,
     * whichever panel it names, so its close guard is asked first.
     */
    subjectProps: string[];
}

/**
 * A panel config as its author writes it: everything optional but the identity,
 * and `width` only the bounds they mean to change. definePanel fills the rest
 * in, which is the difference between this and PanelConfig.
 */
export type PanelConfigInput =
    & Pick<PanelConfig, 'id' | 'title'> &
    Partial<Omit<PanelConfig, 'id' | 'title' | 'width' | 'excludes'>> &
    {
        width?: Partial<PanelWidth>;
        /** Declared centrally in exclusions.ts; a per-panel one is rejected. */
        excludes?: never;
    };

/** What the registry holds per panel: its descriptor and its component. */
export interface PanelEntry {
    config: PanelConfig;
    Component: ComponentType<Record<string, unknown>>;
}

/**
 * Any registry the resolvers work over. The shipped one is keyed by `PanelId`;
 * a resolver test builds its own out of synthetic ids, which is why this and
 * `OpenPanel.id` below stay open — the algorithms are not about real panels.
 */
export type PanelRegistry = Record<string, PanelEntry>;

/**
 * Per-panel props kept across a close and reopen, keyed by panel id. Private
 * to the browser: these name org entities and never ride a shared link.
 */
export type PanelLocalProps = Partial<Record<PanelId, Record<string, unknown>>>;

/** The props that ride the URL's search string, shared across open panels. */
export type SharedPanelProps = Record<string, unknown>;

/** The URL props mirrored into storage, keyed as they are spelled in the URL. */
export type PersistedShared = Record<string, string>;

/** The stored record of the shell, as `migrate` normalizes it. */
export interface ShellRecord {
    navWidth: number;
    chatWidth: number;
    openPanels: PanelId[];
    scratch: PanelLocalProps;
    focusedId: PanelId | null;
    userWidths: UserWidths;
    shared: PersistedShared;
}

/** What an open can mean beyond "put this panel on the stage". */
export interface OpenPanelOptions {
    /** The mode the open resolves under, when the candidate supports it. */
    mode?: PanelMode;
    /** Panels opened in the SAME commit, after the candidate. */
    also?: PanelId[];
    /**
   * Which of the opened panels the stage should hold, when it is not the
   * candidate — for a control that stands for a workflow rather than a panel,
   * whose home is the list the user picks from. Only the narrow stage can tell
   * the difference. Ignored when that panel did not end up open.
   */
    focus?: PanelId;
}

/** Opens a panel, as the nav rail, the home launcher and the phone's list all ask for it. */
export type OpenPanelFn = (
    id: PanelId, props?: Record<string, unknown>, options?: OpenPanelOptions,
) => unknown;

/**
 * One destination of the nav rail, which the rail and the home launcher both draw. A panel
 * supplies its own, except the Camera settings workflow, which stands for a form the cameras
 * list anchors.
 */
export interface NavEntry {
    /** The panel the entry stands for: its key in both renderers, and what an open names. */
    id: PanelId;
    label: string;
    description: string;
    icon: ComponentType<{ className?: string }> | null;
    /** The rail half it sits in: what the operator watches with, or configures with. */
    group: PanelMode;
}

/** A tab of a panel that the home launcher offers as a tile of its own. */
interface LauncherTab {
    /** The tab's own value, unique among the tabs of its panel. */
    key: string;
    /** What the tile is called. It names the place without the panel around it. */
    label: string;
    description: string;
    icon: ComponentType<{ className?: string }>;
    /** The props that open the panel on the tab. */
    props: Record<string, unknown>;
    /** An administrator's tab in a panel that each member can open. */
    requiresAdmin?: boolean;
}

/** A tile's key: the entry's id, or the entry's id and the key of its tab. */
export type LauncherPlaceKey = PanelId | `${PanelId}:${string}`;

/** One tile of the home launcher: a rail entry, or one tab of the entry's panel. */
export interface LauncherPlace {
    key: LauncherPlaceKey;
    /** The rail entry that the tile opens. */
    entryId: PanelId;
    label: string;
    description: string;
    icon: ComponentType<{ className?: string }> | null;
    /** The props that open the panel on its tab. An entry with no tabs has none. */
    props?: Record<string, unknown>;
}

/** A fleet count and how much of it is unreachable, as a launcher tile reports it. */
export interface FleetNumbers {
    total: number;
    offline: number;
}

/** The organization's open health issues by severity. */
export interface HealthNumbers {
    errors: number;
    warnings: number;
    notices: number;
}

/** What the home launcher's tiles say about the organization beyond their own description. */
export interface LauncherNumbers {
    cameras: FleetNumbers;
    servers: FleetNumbers;
    health: HealthNumbers;
    /** The stream is down: an issue may have ended unseen. */
    isHealthStale: boolean;
    recentActivity: RecentActivitySnapshot;
}

/** What a launcher tile draws under its description. */
export enum LauncherInfoKind {
    Fleet = 'fleet',
    Health = 'health',
    Activity = 'activity',
}

export interface FleetInfo extends FleetNumbers {
    kind: LauncherInfoKind.Fleet;
    /** What one device of the fleet is called, in the singular. */
    noun: string;
}

export interface HealthInfo extends HealthNumbers {
    kind: LauncherInfoKind.Health;
}

export interface ActivityInfo extends ActivityCount {
    kind: LauncherInfoKind.Activity;
}

export type LauncherInfo = FleetInfo | HealthInfo | ActivityInfo;

/** A launcher tile's size, in grid units on each side: the column it stands in decides it. */
export enum LauncherTileSize {
    Small = 1,
    Medium = 2,
    Large = 3,
}

/** Which pane a settings panel with a rail shows while it is too narrow for both. */
export enum CompactPane {
    List = 'list',
    Section = 'section',
}

/** One launcher tile's place in the grid of slots, 1-based as CSS grid lines are. */
export interface LauncherSlot {
    id: LauncherPlaceKey;
    column: number;
    row: number;
    columnSpan: number;
    rowSpan: number;
    /** The drawn height, which decides how much text the tile has room for. */
    heightPx: number;
}

/** One state a launcher figure counts: its marks in the graphic and its line in the key. */
export interface LauncherFigurePart {
    label: string;
    count: number;
    /** The Tailwind background class of its marks. */
    fill: string;
}

/** What the rail and the launcher know of the stage when they light an entry or open it. */
export interface NavOpenState {
    /** The panels on the stage right now. */
    visibleIds: ReadonlySet<PanelId>;
    /** The panels that are open, which on a stage too narrow for the row is more than that. */
    openIds: ReadonlySet<PanelId>;
}

/** One open panel: its id, the props it was opened with, and its descriptor. */
export interface OpenPanel {
    id: PanelId;
    props: Record<string, unknown>;
    config: PanelConfig;
}

/** The stage's veto on an automatic open: could it show `addition` beside `panels`? */
export type PanelAdmits = (panels: OpenPanel[], addition: OpenPanel) => boolean;

export interface PropWrite {
    id: string;
    props: Record<string, unknown>;
}

/** A change of the open set that `planOpen` (shellOpenPlan.ts) decides the outcome of. */
export type OpenRequest = {
    kind: 'open';
    id: PanelId;
    props?: Record<string, unknown>;
    options?: OpenPanelOptions;
    /** Declined when the stage cannot show the panel beside the focus. */
    isOffer?: boolean;
} | { kind: 'props'; id: PanelId; props: Record<string, unknown> } | { kind: 'autoChild' };

export type PanelOpenRequest = Extract<OpenRequest, { kind: 'open' }>;
export type PanelPropsRequest = Extract<OpenRequest, { kind: 'props' }>;

/** A request that always has a plan: a prop write, or an open that is not an offer. */
export type DefiniteRequest = PanelPropsRequest | (PanelOpenRequest & { isOffer?: false });

/** `planOpen` over the current state. */
export interface OpenPlanner {
    (request: DefiniteRequest): OpenPlan;
    (request: OpenRequest): OpenPlan | null;
}

export interface OpenPlanInput {
    openPanels: OpenPanel[];
    mode: PanelMode;
    isSmViewport: boolean;
    focusedId: PanelId | null;
    localPropsByPanel: PanelLocalProps;
    admits: (anchor: PanelId | null) => PanelAdmits;
    registry: PanelRegistry;
    request: OpenRequest;
}

export interface OpenPlan {
    /** In the order of `openPanels`, then the appended panels. */
    nextPanels: OpenPanel[];
    /** The panels that close or whose subject changes, with their dependents. */
    departingIds: PanelId[];
    focusId: PanelId | null;
    /** The URL props of the target: only the keys that the request names. */
    urlProps: SharedPanelProps;
    /** The other props of the target, for the record. */
    localProps: Record<string, unknown>;
    shouldReplace: boolean;
}

/** The stage's measured width, and whether the window is phone-sized. */
export interface StageLayout {
    stageWidth: number;
    isPhone: boolean;
}

/** Where the shell put a camera pick. */
export enum CameraPickAction {
    /** Onto a fresh view, leaving any scene behind. */
    Open = 'open',
    /** Onto a camera surface already showing, in place of its camera. */
    Retarget = 'retarget',
    /** Beside the cameras already on the scene. */
    Add = 'add',
    /** To the scene, which decides how to bring a moment of that camera forward. */
    Focus = 'focus',
}

/** What a camera pick asks of the shell, beyond the camera itself. */
export interface CameraSelection {
    /** The moment to park playback at, UTC ms. */
    seekAtMs?: number;
    /** Back to live, wherever playback was parked. */
    isLive?: boolean;
    /** Cameras opened beside this one, as one scene: what a viewing session showed. */
    also?: string[];
    /** A window to frame the moment in. */
    zoom?: { startMs?: number; endMs?: number } | null;
    /** A double-click: leaves a scene behind for this one camera. */
    shouldReplace?: boolean;
    /**
     * What the view's archive is cut to. A pick naming another scope than the view's leaves
     * the cameras on screen behind for its own.
     */
    scope: ViewScope;
}

/** Hands a camera pick to the shell, and says where it landed. */
export type SelectCamera = (
    cameraId: string, opts: CameraSelection,
) => CameraPickAction | undefined;

/** The one write a camera pick makes. */
export type CameraPickWrite =
    | { kind: 'open'; props: Record<string, unknown>; mode?: PanelMode } |
    { kind: 'props'; panelId: PanelId; props: Record<string, unknown> };

export interface CameraPickInput {
    cameraId: string;
    selection: CameraSelection;
    openPanels: OpenPanel[];
    mode: PanelMode;
    visibleIds: ReadonlySet<PanelId>;
    /** The nonce that the seek and the zoom of one pick share. */
    nowMs: number;
}

export interface CameraPickPlan {
    write: CameraPickWrite;
    /** The hint of the double-click: shown after a join only. */
    isJoinHintShown: boolean;
    /** Null when the panel is visible already. */
    focusId: PanelId | null;
    action: CameraPickAction;
}

/**
 * A departure a panel's close guard took over. The guard now owns the
 * transition, and resumes it once the user has answered.
 */
export interface DeferredDeparture {
    deferred: true;
    kind: string;
}

/** A panel as the chat's tool catalog lists it. */
export interface PanelSummary {
    id: PanelId;
    title: string;
    /** Its URL-synced props. */
    props: string[];
}

/**
 * What PanelHost gives a panel's children. Deliberately minimal, and
 * referentially stable across unrelated panel state changes — a consumer must
 * not re-render because some other panel opened.
 */
export interface PanelContextValue {
    panelId: PanelId;
    mode: PanelMode;
    openPanel: (
        id: PanelId, props?: Record<string, unknown>, options?: OpenPanelOptions,
    ) => void;
    /** Updates one of this panel's own props. */
    setProp: (key: string, value: unknown) => void;
    close: () => void;
    /** Closes another panel — for one that drives a drawer it owns. */
    closePanel: (id: PanelId) => void;
    /** Sets a prop on another panel, the counterpart to `closePanel`. */
    setPanelProp: (id: PanelId, key: string, value: unknown) => void;
    /** Hands a camera pick back to the shell, which decides where it lands. */
    selectCamera: SelectCamera;
    /** Widens this panel over its right-hand neighbours. */
    expand: () => void;
    /** Whether expanding would change anything. */
    canExpand: boolean;
    /**
     * Widens this panel by `byPx` when the free room at the right end of the row holds all of
     * it. Closes no panel and narrows none. Returns whether the panel widened.
     */
    widen: (byPx: number) => boolean;
    /** The host's action row, for `<PanelActions>` to portal into. */
    actionsNode: HTMLElement | null;
    /** Withdraws the host's × while the content offers a better way out. */
    setCloseHidden: (isHidden: boolean) => void;
    /** Draws a brand line inside the panel's edge while its content is in a mode of its own. */
    setOutlined: (isOutlined: boolean) => void;
}

/**
 * What the shell gives every panel host, on the stage or in the chat drawer. PanelHost themes its
 * chrome with it and builds PanelContext from it.
 */
export interface PanelHostBindings {
    mode: PanelMode;
    theme: Theme;
    /** The host's ×, and the context's `close` and `closePanel`. */
    onClose: (id: PanelId) => void;
    openPanel: PanelContextValue['openPanel'];
    setPanelProp: ShellControl['setPanelProp'];
    selectCamera: SelectCamera;
}

/**
 * The wider surface AppShell publishes for the chat panel's UI-tool
 * executors, which drive arbitrary panels rather than only their own.
 */
export interface ShellControl {
    /**
   * The view state the open produced, so the agent can check the panel really
   * opened — or the deferral, when a panel's guard is asking the user first.
   */
    openPanel: (
        id: PanelId, props?: Record<string, unknown>, opts?: OpenPanelOptions,
    ) => ViewState | DeferredDeparture;
    closePanel: (id: PanelId) => DeferredDeparture | void;
    setPanelProp: (id: PanelId, key: string, value: unknown) => DeferredDeparture | void;
    getViewState: () => ViewState;
    /** The gate: the only way an id from the URL or the model becomes a `PanelId`. */
    doesPanelExist: (id: string) => id is PanelId;
    listPanels: () => PanelSummary[];
}

/**
 * The shell's state as the agent's get_view_state tool sees it: ids and
 * resolved props only, with the heavy config dropped.
 */
export interface ViewState {
    focusedId: PanelId | null;
    openPanels: { id: PanelId; props: Record<string, unknown> }[];
}

/**
 * An open panel once the allocator has sized and placed it: its width bounds
 * flattened out of the config, the pixels it got, and where the row puts it.
 */
export interface AllocatedPanel extends OpenPanel {
    min: number;
    max: number;
    /** Takes the room left over once every other panel has its own. */
    fill: boolean;
    /** Saved at MAX_PANEL_WIDTH: takes the leftover room ahead of the fill panels. */
    isExpanded: boolean;
    widthPx: number;
    startPx: number;
}

/** Where a panel is in its entrance or exit on the stage. */
export enum PanelTransitionState {
    /** Mounted, not yet painted in its slot. */
    Entering = 'entering',
    /** Easing into its slot. */
    Appearing = 'appearing',
    Stable = 'stable',
    /** Fading out; still mounted until the exit finishes. */
    Exiting = 'exiting',
}

/** What the stage renders: every slot still on screen, and where each is in its transition. */
export interface StagePresence<T extends { id: string }> {
    /** The allocation these items were reconciled against. */
    source: T[];
    items: T[];
    states: Record<string, PanelTransitionState>;
}

/**
 * One slot of the drag-to-resize row: every open panel flattened to the
 * fields a drag trades in, followed by the stage's trailing free space, which
 * carries no id and no title because it is not a panel.
 */
export interface ResizeSlot {
    id: string | null;
    title: string | null;
    widthPx: number;
    min: number;
    max: number;
    fill: boolean;
}

/** A resize-row slot once the row has been laid out left to right. */
export type PlacedResizeSlot = ResizeSlot & { startPx: number };

/**
 * What the embedded rule editor exposes to the panel hosting it: the unsaved
 * state it owns, and the two ways to resolve it before leaving.
 */
export interface RuleEditorHandle {
    isDirty: () => boolean;
    apply: () => Promise<void>;
    reset: () => void;
}

/** Why a panel is being asked to leave. */
export interface DepartureIntent {
    kind: string;
    /** Gives up a pending step of history: the URL stays where it is. */
    stay?: () => void;
    [key: string]: unknown;
}

/** A departure that waits for a panel's draft, until the panel's dialog answers. */
export interface DraftDeparture {
    kind: string;
    /** Lets the departure go: the transition runs, or the pending history step completes. */
    proceed: () => void;
    /** Keeps the panel as it is: a pending history step is given up. */
    stay: () => void;
}

/**
 * The unsaved work of a panel. A departure that would lose it waits: the panel takes ownership
 * of `resume`, answers the user first, then resumes the transition it interrupted.
 */
export interface PanelCloseGuard {
    /** Asks nothing of the user: a page reload reads it too. */
    hasUnsavedWork: () => boolean;
    defer: (intent: DepartureIntent & { resume: () => unknown; stay?: () => void }) => void;
}

/**
 * What is being dragged across the stage. Only cameras today, hence the
 * discriminant. A row that shows no moment carries null.
 */
export interface CameraDragPayload {
    kind: 'camera';
    deviceId: string;
    name?: string;
    t?: number | null;
}

/**
 * The window a camera_view's timeline is currently showing, for a list panel
 * that scopes its query to it. Nulls mean no view is publishing — the panel is
 * closed, or its timeline has no range yet.
 */
export interface ViewTimeline {
    cameraId: string | null;
    start: number | null;
    end: number | null;
    /** When the view reported this window: the "now" its end is compared with. */
    reportedAt: number | null;
}

/**
 * The view panel's `command` prop: an instruction its scene carries out once.
 * `show` replaces whatever is on screen with the camera; `add` puts it beside
 * the rest, at `seekAtMs` if one is given; `focus` brings it forward
 * wherever it is. The nonce is what makes a repeat of the same instruction a
 * new one.
 */
interface CameraCommandBase {
    deviceId: string;
    nonce: number;
}

export interface ShowCameraCommand extends CameraCommandBase {
    kind: 'show';
    /** Cameras shown beside it, as one scene. */
    alsoDeviceIds?: string[];
}

/**
 * `join` is the softer add a plain click on a scene means: a camera the scene
 * already holds comes forward instead of arriving a second time. An add leaves
 * it off - a layout may want one camera in two places.
 */
export interface AddCameraCommand extends CameraCommandBase {
    kind: 'add';
    /** The moment to seek to, in ms (UTC epoch). */
    seekAtMs: number | null;
    shouldJoin: boolean;
}

export interface FocusCameraCommand extends CameraCommandBase {
    kind: 'focus';
    /** The moment to seek to, in ms (UTC epoch). */
    seekAtMs: number | null;
}

export type CameraCommand = ShowCameraCommand | AddCameraCommand | FocusCameraCommand;

/** The header row's two sizes: the view's full width, or a camera's tile. */
export type HeaderSize = 'panel' | 'tile';
